import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `data-screen-label="Agent"`, tab `history` — the messages a caller left and the request for a person, inside a
 * conversation's detail (plan-018 WAPP-B, design §30.12, ADR 0060). No new screen: both sit in the History detail.
 *
 * ONE CARD PER MESSAGE, EVERYTHING ABOUT IT TOGETHER: who (the name), how to reach them (the contact, marked "Not
 * confirmed" when a spoken contact did not match what was recognised), the message, and — for a caller who asked for a
 * person — the summary prepared for that person, with the time it was left. After-hours and busy messages are the
 * caller's words with the calling number when known and no name, so their name row says so rather than "none given".
 *
 * WHAT A ROLE DOES NOT READ IS SAID, NEVER LEFT BLANK. A role that does not read transcripts sees that a message was left,
 * what led to it, the kind of contact and whether it was confirmed — and "Hidden for your role" where the words would be.
 * A role that does not read caller details sees "Hidden for your role" in place of a calling number. A message retention
 * removed says so.
 *
 * A HANDED-OVER CONVERSATION (`handoff`) reads "Handed to a person" with the summary the person heard; one still being put
 * through, one nobody answered and one sent as a priority message (web chat, website voice and test chats) each say what
 * happened.
 */
import { Icon, Tag } from "@wetalk/ui";
import type { ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import { displayNumberOf } from "../../numbers/number-display.js";
import type {
  ConversationHandoffView,
  TakenMessageContactKind,
  TakenMessageOfferedBy,
  TakenMessageView,
} from "../../../api/taken-message-view.js";

import "./message-card.css";

/** What led to the message, as its card's heading says it. */
export function offeredByWordOf(offeredBy: TakenMessageOfferedBy): string {
  switch (offeredBy) {
    case "in_conversation":
      return portalText("operations", "taken_message.offered_in_conversation");
    case "handoff":
      return portalText("operations", "taken_message.offered_handoff");
    case "after_hours":
      return portalText("operations", "taken_message.offered_after_hours");
    case "overflow":
      return portalText("operations", "taken_message.offered_overflow");
  }
}

/** The kind of contact, as the contact row's label says it. */
export function contactKindWordOf(kind: TakenMessageContactKind): string {
  switch (kind) {
    case "calling_number":
      return portalText("operations", "taken_message.contact_calling_number");
    case "phone":
      return portalText("operations", "taken_message.contact_phone");
    case "email":
      return portalText("operations", "taken_message.contact_email");
  }
}

/** The contact as it is read: a phone number grouped, an email address as it is. */
export function contactValueTextOf(kind: TakenMessageContactKind | null, value: string): string {
  return kind === "email" ? value : displayNumberOf(value);
}

/** `14:02` on the day the conversation is shown for. */
function clockOf(isoTimestamp: string): string {
  const at = new Date(isoTimestamp);
  if (Number.isNaN(at.getTime())) return isoTimestamp;
  return `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
}

/** The name row: the caller's name, or why there is none. */
export function callerNameTextOf(message: TakenMessageView): string {
  if (message.callerName !== null) return message.callerName;
  return message.offeredBy === "after_hours" || message.offeredBy === "overflow"
    ? portalText("operations", "taken_message.name_not_asked")
    : portalText("operations", "taken_message.name_not_given");
}

/** The contact row: the value, "Hidden for your role", or that no way to reach them was left. */
export function contactTextOf(message: TakenMessageView): string {
  if (message.contactWithheld) return portalText("operations", "taken_message.hidden_for_role");
  if (message.contactValue === null) return portalText("operations", "taken_message.contact_none");
  return contactValueTextOf(message.contactKind, message.contactValue);
}

export function TakenMessageCard({ message }: { readonly message: TakenMessageView }): ReactNode {
  usePortalTranslation("operations");
  const glyph = message.offeredBy === "handoff" ? GLYPH.headset : GLYPH.bellRinging;
  return (
    <div className="c-msg-card" data-offered-by={message.offeredBy}>
      <div className="c-msg-card-head">
        <span className="c-msg-card-mark">
          <Icon glyph={glyph} size="lg" />
        </span>
        <span className="c-msg-card-title">{offeredByWordOf(message.offeredBy)}</span>
        <span className="c-msg-card-at">{clockOf(message.createdAt)}</span>
      </div>

      {message.blanked ? (
        <p className="c-msg-card-note">{portalText("operations", "taken_message.blanked")}</p>
      ) : (
        <dl className="c-msg-card-facts">
          <div>
            <dt>{portalText("operations", "taken_message.label_name")}</dt>
            <dd data-withheld={message.withheld ? "true" : undefined}>
              {message.withheld ? portalText("operations", "taken_message.hidden_for_role") : callerNameTextOf(message)}
            </dd>
          </div>
          <div>
            <dt>
              {message.contactKind === null
                ? portalText("operations", "taken_message.label_contact")
                : contactKindWordOf(message.contactKind)}
            </dt>
            <dd data-withheld={message.contactWithheld ? "true" : undefined}>
              <span className="c-msg-card-contact">{contactTextOf(message)}</span>
              {message.contactVerified || message.contactKind === null ? null : (
                <Tag status="caution">{portalText("operations", "taken_message.not_confirmed")}</Tag>
              )}
            </dd>
            {message.contactVerified || message.contactKind === null ? null : (
              <dd className="c-msg-card-fact-note">{portalText("operations", "taken_message.not_confirmed_note")}</dd>
            )}
          </div>
          <div className="c-msg-card-wide">
            <dt>{portalText("operations", "taken_message.label_message")}</dt>
            <dd className="c-msg-card-text" data-withheld={message.withheld ? "true" : undefined}>
              {message.withheld
                ? portalText("operations", "taken_message.hidden_for_role")
                : (message.messageText ?? portalText("operations", "taken_message.message_none"))}
            </dd>
          </div>
          {message.handoffSummary === null ? null : (
            <div className="c-msg-card-wide">
              <dt>{portalText("operations", "taken_message.label_summary")}</dt>
              <dd className="c-msg-card-text">{message.handoffSummary}</dd>
            </div>
          )}
        </dl>
      )}
      {message.withheld && !message.blanked ? (
        <p className="c-msg-card-note">{portalText("operations", "taken_message.withheld_note")}</p>
      ) : null}
    </div>
  );
}

/** What happened to the caller's request for a person, in a sentence. */
export function handoffTextOf(handoff: ConversationHandoffView): string {
  const path =
    handoff.path === "sip_trunk"
      ? portalText("operations", "handoff.path_sip_trunk")
      : handoff.path === "carrier"
        ? portalText("operations", "handoff.path_carrier")
        : null;
  switch (handoff.outcome) {
    case "joined":
      return path === null
        ? portalText("operations", "handoff.joined")
        : portalText("operations", "handoff.joined_path", { v0: path });
    case "in_progress":
      return portalText("operations", "handoff.in_progress");
    case "not_joined":
      return portalText("operations", "handoff.not_joined");
    case "priority_message":
      return portalText("operations", "handoff.priority_message");
  }
}

export function HandoffCard({ handoff }: { readonly handoff: ConversationHandoffView }): ReactNode {
  usePortalTranslation("operations");
  return (
    <div className="c-msg-card" data-handoff={handoff.outcome}>
      <div className="c-msg-card-head">
        <span className="c-msg-card-mark">
          <Icon glyph={GLYPH.headset} size="lg" />
        </span>
        <span className="c-msg-card-title">
          {handoff.outcome === "joined"
            ? portalText("operations", "handoff.title_joined")
            : portalText("operations", "handoff.title_requested")}
        </span>
        {handoff.joinedAt === null ? null : <span className="c-msg-card-at">{clockOf(handoff.joinedAt)}</span>}
      </div>
      <p className="c-msg-card-note">{handoffTextOf(handoff)}</p>
      {handoff.summary === null ? null : (
        <dl className="c-msg-card-facts">
          <div className="c-msg-card-wide">
            <dt>{portalText("operations", "handoff.label_summary")}</dt>
            <dd className="c-msg-card-text">{handoff.summary}</dd>
          </div>
        </dl>
      )}
    </div>
  );
}

/** A History row's mark: a caller left a message in it. */
export function TakenMessageRowTag(): ReactNode {
  usePortalTranslation("operations");
  return <Tag tone="accent">{portalText("operations", "taken_message.row_tag")}</Tag>;
}
